import { ArrowBackOutlined, ArrowForwardOutlined, DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import { ConditionalWrapper } from '../../../../../common/ConditionalWrapper';
import { VyConfirm } from '../../../../../common/VyConfirm';
import VyPrivilegeWrapper from '../../../../../common/VyPrivilegeWrapper';
import VyTooltip from '../../../../../common/VyTooltip';

// Padding added to separate the control border from the component for a better UX
const RESIZE_PADDING = 12;
interface NestedComponentControlProps {
  firstComponent: boolean;
  lastComponent: boolean;
  onMoveBack: () => void;
  onMoveForward: () => void;
  onEdit: () => void;
  onDelete: () => void;
  children?: ReactNode;
}

const NestedComponentControl = (props: NestedComponentControlProps) => {
  const { firstComponent, lastComponent, children, onMoveBack, onMoveForward, onEdit, onDelete } = props;
  const templateEditMode = useSelector((state) => state.common.templateEditMode);

  const { t } = useTranslation('pagebuilder');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [showUI, setShowUI] = useState(templateEditMode);
  const [animateShow, setAnimateShow] = useState(false);
  const [showControl, setShowControl] = useState(false);

  useEffect(() => {
    animateUIControlButtons();
  }, [templateEditMode]);

  const animateUIControlButtons = () => {
    if (!templateEditMode) {
      /**
       * we let the animation hide the conrtols
       * only then we change display to none
       */
      setAnimateShow(false);
      setTimeout(() => {
        setShowUI(false);
      }, 300);
    } else {
      setShowUI(true);
      /**
       * timeout for display to change from none to flex, only then the animation is triggered
       * otherwise the animation will look buggy
       */
      setTimeout(() => {
        setAnimateShow(true);
      }, 50);
    }
  };

  return (
    <div onMouseEnter={() => setShowControl(true)} onMouseLeave={() => setShowControl(false)}>
      <div
        className={`flex flex-row  opacity-${showControl && showUI ? '100' : '0'} ${showUI ? 'flex' : 'hidden'} flex-row transition-all w-full p-0 h-${
          animateShow ? '8' : '0'
        } justify-end transition-all `}>
        <div className="flex flex-row items-center h-full bg-vystemblue rounded-tl-xl">
          <VyPrivilegeWrapper privilege="pagebuilder">
            {(access) => (
              <VyTooltip title={access ? t('edit-component-title') : ''}>
                <Button
                  disabled={!access}
                  size="small"
                  variant="contained"
                  sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                  disableElevation
                  onClick={onEdit}>
                  <EditOutlined fontSize="small" />
                </Button>
              </VyTooltip>
            )}
          </VyPrivilegeWrapper>
          {!firstComponent && (
            <VyTooltip title={t('sort-component-title')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={onMoveBack}>
                <ArrowBackOutlined fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          {!lastComponent && (
            <VyTooltip title={t('sort-component-title')}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                disableElevation
                onClick={onMoveForward}>
                <ArrowForwardOutlined fontSize="small" />
              </Button>
            </VyTooltip>
          )}
          <VyPrivilegeWrapper privilege="pagebuilder">
            {(access) => (
              <VyConfirm disabled={!access} onConfirm={onDelete}>
                <VyTooltip title={access ? t('delete-component-title') : ''}>
                  <Button
                    disabled={!access}
                    size="small"
                    variant="contained"
                    sx={{ background: 'transparent', ':hover': { backgroundColor: 'transparent' }, padding: 0 }}
                    disableElevation>
                    <DeleteOutlined fontSize="small" />
                  </Button>
                </VyTooltip>
              </VyConfirm>
            )}
          </VyPrivilegeWrapper>
        </div>
      </div>
      <div
        ref={wrapperRef}
        className={`relative `}
        style={{ display: 'flex', justifyContent: 'center', scrollbarWidth: 'none', msOverflowStyle: 'none', padding: templateEditMode ? RESIZE_PADDING : 0 }}>
        <ConditionalWrapper
          condition={templateEditMode}
          wrapper={(children) => (
            <>
              {templateEditMode && (
                <div
                  className={`absolute top-0 left-0 w-full h-full border-2  ${showControl ? 'opacity-100' : 'opacity-0'} transition-opacity overflow-hidden`}
                  style={{ borderColor: 'var(--vystem-blue)' }}>
                  <div className="absolute p-4 -bottom-6 -right-6">
                    <div className="w-2 h-2 bg-white border-2 " style={{ borderColor: 'var(--vystem-blue)' }} />
                  </div>
                  <div className="absolute p-4 -bottom-6 -left-6">
                    <div className="w-2 h-2 bg-white border-2 " style={{ borderColor: 'var(--vystem-blue)' }} />
                  </div>
                </div>
              )}
              {children}
            </>
          )}>
          <>{children}</>
        </ConditionalWrapper>
      </div>
    </div>
  );
};

export default NestedComponentControl;
